import React from "react";
import {
	ArrayPick,
	Button,
	ClassNameProps,
	Column,
	If,
	isAfter,
	makeClassName,
	Modal,
	RelativeTimestamp,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { NewBadge } from "../../../../Components/Badge";
import { Announcement, useAnnouncementSocialItemsQuery, User, UserNameAndImageFragment } from "../../../../Utilities";
import { UserFormat } from "../../../Format";
import { MediaAttachmentUtilities } from "../../../Utilities";
import * as ModelComponents from "../../_General";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import * as SocialReactionComponents from "../../SocialReaction";
import * as UserComponents from "../../User";
import { AnnouncementAdminModal } from "../AdminModal";
import { AnnouncementModal } from "../Modal";
import { AnnouncementStatusBadge } from "../StatusBadge";
import styles from "./styles.module.css";

export type AnnouncementInChatBubble = Pick<Announcement, "id" | "title" | "body" | "startTime" | "endTime"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	mediaAttachments: ArrayPick<
		Announcement["mediaAttachments"],
		"id" | "title" | "isFile" | "fileURL" | "fileKey" | "fileContentType" | "linkURL"
	>;
};

export type ChatBubbleProps = {
	announcement: AnnouncementInChatBubble;
	isNew?: boolean;
	withModalBackButton?: boolean;
	bubbleClassName?: string;
	bubbleContentClassName?: string;
	modalVariant?: "default" | "admin";
} & Partial<ClassNameProps & StyleProps>;

export function ChatBubble({
	announcement,
	isNew,
	withModalBackButton,
	bubbleClassName,
	bubbleContentClassName,
	modalVariant = "default",
	className,
	style,
}: ChatBubbleProps) {
	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitAndExtractMediaAttachments(announcement.mediaAttachments, announcement.body),
		[announcement.mediaAttachments, announcement.body],
	);

	const { data: socialData } = useAnnouncementSocialItemsQuery({ variables: { id: announcement.id } });

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex } =
		MediaAttachmentComponents.MediaAttachmentModal.useModal();

	const ModalComponent = modalVariant === "admin" ? AnnouncementAdminModal : AnnouncementModal;

	return (
		<div className={makeClassName(styles["chat-bubble"], className)} style={style}>
			<Row justify="spaced-start" align="center" horizontalSpacing="1.25rem">
				{announcement.author && (
					<UserComponents.UserIcon
						user={announcement.author}
						size={60}
						className={styles["chat-bubble__author-image--outer"]}
					/>
				)}

				<div className={makeClassName(styles["chat-bubble__bubble"], bubbleClassName)}>
					<Column
						justify="spaced-start"
						className={makeClassName(styles["chat-bubble__bubble__content"], bubbleContentClassName)}
					>
						<Row justify="spaced-start" horizontalSpacing="0.5rem">
							<AuthorTimestampView announcement={announcement} isNew={isNew} style={{ minWidth: 0 }} />
							<If condition={!!announcement.endTime && isAfter(new Date(), announcement.endTime)}>
								<AnnouncementStatusBadge announcement={announcement} size="small" />
							</If>
						</Row>

						<If condition={announcement.body !== ""}>
							<ModelComponents.TruncatedTextWithGeneralButton
								lines={4}
								onClick={openModal}
								className={styles["chat-bubble__body-text"]}
							>
								{announcement.body}
							</ModelComponents.TruncatedTextWithGeneralButton>
						</If>

						<If condition={visualAttachments.length > 0}>
							<MediaAttachmentComponents.VisualAttachmentsRow
								attachments={visualAttachments}
								onClickAttachment={onClickMediaAttachment}
							/>

							<Modal.If condition={isMediaAttachmentModalOpen}>
								<MediaAttachmentComponents.MediaAttachmentModal
									mediaAttachments={visualAttachments}
									initialIndex={mediaAttachmentModalIndex}
									backButtonText="Back to Announcements"
									onClose={closeMediaAttachmentModal}
								/>
							</Modal.If>
						</If>
						<If condition={fileAttachments.length > 0}>
							<MediaAttachmentComponents.FileAttachmentsRow
								attachments={fileAttachments}
								attachmentClassName={styles["chat-bubble__file-attachment"]}
							/>
						</If>
						<If condition={linkAttachments.length > 0}>
							<MediaAttachmentComponents.LinkAttachmentsRow
								attachments={linkAttachments}
								attachmentClassName={styles["chat-bubble__link-attachment"]}
							/>
						</If>

						<Row justify="space-between">
							<SocialReactionComponents.ReactionsView
								announcementID={announcement.id}
								reactions={socialData?.announcement.socialReactions ?? []}
							/>

							<Button variant="link" size="small" onClick={openModal}>
								View
							</Button>

							<Modal.If condition={isModalOpen}>
								<ModalComponent
									announcement={announcement}
									isNew={isNew}
									withBackButton={withModalBackButton}
									onClose={closeModal}
								/>
							</Modal.If>
						</Row>
					</Column>
				</div>
			</Row>
		</div>
	);
}

type AuthorTimestampViewProps = {
	announcement: Pick<AnnouncementInChatBubble, "id" | "startTime" | "author">;
	isNew?: boolean;
} & Partial<ClassNameProps & StyleProps>;

function AuthorTimestampView({ announcement, isNew = false, className, style }: AuthorTimestampViewProps) {
	return (
		<Row justify="spaced-start" className={className} style={style}>
			<Row justify="spaced-start" align="center" style={{ minWidth: 0 }}>
				{announcement.author && (
					<UserComponents.UserIcon
						user={announcement.author}
						size={32}
						className={styles["chat-bubble__author-image--inner"]}
					/>
				)}

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ minWidth: 0 }}>
					<Row justify="spaced-start" align="center">
						<TruncatedText style={{ minWidth: 0 }}>
							{announcement.author && (
								<strong className={styles["chat-bubble__author-name"]}>{UserFormat.name(announcement.author)}</strong>
							)}
						</TruncatedText>
					</Row>
					<RelativeTimestamp
						value={announcement.startTime}
						className={styles["chat-bubble__timestamp"] + " timestamp"}
					/>
				</Column>
			</Row>

			<If condition={isNew}>
				<NewBadge size="small" />
			</If>
		</Row>
	);
}
